import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MonIpm from '../components/ipm/MonIpm';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
// Suivi « prochain acteur » (vue salarié de GET /api/prets-sogas/mes-demandes).
import { SuiviDemande, suiviEnCours } from '../components/suivi/SuiviDemande';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const STATUTS_DEMANDE_LABEL = {
  soumis:          'En attente de décision',
  approuve_dg:     'Approuvé par la Direction',
  traite_rh:       'En traitement RH',
  transmis_compta: 'Préparation du virement',
  fonds_debloques: 'Fonds débloqués',
  actif:           'En cours de remboursement',
  refuse:          'Refusée',
  solde:           'Soldée',
  annule:          'Annulée',
};

// ─── Utilitaires ─────────────────────────────────────────────────────────
const formatFCFA = n =>
  new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' FCFA';

const fmtDateMoisAnnee = d => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const fmtDateCourte = d => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const aujourd_hui = () => new Date().toLocaleDateString('fr-FR', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
});

const prochainPrelevement = () => {
  const now = new Date();
  const mois = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  const m = now.getDate() >= 15 ? new Date(now.getFullYear(), now.getMonth() + 1, 15) : new Date(now.getFullYear(), now.getMonth(), 15);
  const diff = Math.ceil((m - now) / (1000 * 60 * 60 * 24));
  return { label: `15 ${mois[m.getMonth()]} ${m.getFullYear()}`, jours: diff };
};

// ─── SVG Icons ────────────────────────────────────────────────────────────
function Ico({ name, size = 16, color = 'currentColor', stroke = 1.8 }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths = {
    calendar:   <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    creditCard: <><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></>,
    dollar:     <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7H14a3.5 3.5 0 1 1 0 7H6"/></>,
    activity:   <><path d="M3 12h4l3-9 4 18 3-9h4"/></>,
    calCheck:   <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M8 15h3"/></>,
    home:       <><path d="M3 10l9-7 9 7v10a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2V10z"/></>,
    bag:        <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></>,
    shield:     <><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/><path d="M9 12l2 2 4-4"/></>,
    arrowR:     <><path d="M5 12h14M13 5l7 7-7 7"/></>,
    chevR:      <><path d="m9 18 6-6-6-6"/></>,
    chevDown:   <><path d="m6 9 6 6 6-6"/></>,
    info:       <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
    phone:      <><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7 12.8 12.8 0 0 0 .7 2.8 2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4 12.8 12.8 0 0 0 2.8.7A2 2 0 0 1 22 16.9z"/></>,
    check:      <><path d="M20 6 9 17l-5-5"/></>,
    backArrow:  <><path d="M19 12H5M12 5l-7 7 7 7"/></>,
    clock:      <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
    mail:       <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></>,
  };
  return <svg {...p}>{paths[name]}</svg>;
}

// ─── Sous-composants (HORS composant principal — react-component-rules) ──

function BadgeStatut({ statut }) {
  const s = {
    active:     { bg: '#E8F5E9', color: '#144A18', label: 'Active', dot: '#1B5E20' },
    suspendue:  { bg: '#F5F5F5', color: '#616161', label: 'Suspendue', dot: '#9E9E9E' },
    soldee:     { bg: '#EEEEEE', color: '#424242', label: 'Soldée', dot: '#616161' },
    en_attente: { bg: '#FFF3E0', color: '#E65100', label: 'En cours de validation', dot: '#E65100' },
    validee:    { bg: '#E3F2FD', color: '#0D47A1', label: 'Validée', dot: '#1D4ED8' },
    refusee:    { bg: '#FFEBEE', color: '#B71C1C', label: 'Refusée', dot: '#B71C1C' },
    annulee:    { bg: '#FFEBEE', color: '#B71C1C', label: 'Annulée', dot: '#B71C1C' },
  }[statut] || { bg: '#FFF3E0', color: '#E65100', label: 'En attente', dot: '#E65100' };

  return (
    <span style={{ background: s.bg, color: s.color, padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
}

function BarreProgression({ rembourse, initial, suspendue }) {
  const pct = initial > 0 ? Math.min(100, Math.round(rembourse / initial * 100)) : 0;
  return (
    <div style={{ margin: '2px 0 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#7A7A7A', marginBottom: 6, fontVariantNumeric: 'tabular-nums' }}>
        <span><span style={{ color: suspendue ? '#616161' : '#144A18', fontWeight: 600 }}>{pct} %</span> remboursé</span>
        <span>{100 - pct} % restant</span>
      </div>
      <div style={{ height: 8, borderRadius: 99, background: '#EFEFEF', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: suspendue ? '#9E9E9E' : '#1B5E20', borderRadius: 99, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function IcoDette({ type }) {
  const icons = { pret_classique: 'home', pret_tabaski: 'bag', ipm_dette: 'shield', pret_sogas: 'dollar' };
  const icon = icons[type] || 'creditCard';
  return (
    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#E8F1E9', color: '#144A18', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Ico name={icon} size={16} color="#144A18" />
    </div>
  );
}

function DetailExpandable({ dette }) {
  const navigate = useNavigate();
  return (
    <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 4, paddingTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Informations détaillées
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', marginBottom: 14 }}>
        {[
          { k: 'Montant initial', v: formatFCFA(dette.montant_initial) },
          { k: 'Remboursé', v: formatFCFA(dette.montant_rembourse) },
          { k: 'Reste à payer', v: formatFCFA(dette.montant_restant) },
          { k: 'Chaque mois', v: formatFCFA(dette.mensualite_actuelle) },
          { k: 'Date de début', v: fmtDateCourte(dette.date_debut) },
          { k: 'Fin prévue', v: fmtDateMoisAnnee(dette.date_fin_prevue) },
        ].map(({ k, v }) => (
          <div key={k}>
            <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 2 }}>{k}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
          </div>
        ))}
      </div>

      {dette.historique?.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Ce qui a déjà été prélevé
          </div>
          <div>
            {dette.historique.map((h, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: i < dette.historique.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#E8F1E9', color: '#144A18', display: 'grid', placeItems: 'center' }}>
                  <Ico name="check" size={14} color="#144A18" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{fmtDateCourte(h.date)}</div>
                  <div style={{ fontSize: 12, color: '#7A7A7A' }}>{h.label}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  − {formatFCFA(h.montant)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {dette.type_dette === 'pret_sogas' && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
          <button
            onClick={() => navigate('/prets-sogas')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#E8F1E9', border: '1px solid #DDEADC', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#144A18', fontFamily: 'inherit' }}
          >
            <Ico name="arrowR" size={14} color="#144A18" />
            Voir le dossier dans Prêts SOGAS
          </button>
        </div>
      )}
    </div>
  );
}

function CarteDette({ dette, ouvert, onToggle }) {
  const { label: labelProchainRet } = prochainPrelevement();
  const dateDecision = dette.updated_at ? fmtDateCourte(dette.updated_at.slice(0, 10)) : null;

  // --- Carte terminale : refusee, annulee ---
  if (STATUTS_TERMINAUX.has(dette.statut)) {
    return (
      <div style={{ background: '#fff', border: '1px dashed #E0C0C0', borderLeft: '4px dashed #B71C1C', borderRadius: 12, padding: '14px 16px', marginBottom: 12, opacity: 0.7 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <IcoDette type={dette.type_dette} />
            <span style={{ fontWeight: 600, fontSize: 15, color: '#1A1A1A' }}>{dette.type_libelle}</span>
          </div>
          <BadgeStatut statut={dette.statut} />
        </div>
        {dateDecision && <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 8 }}>Le {dateDecision}</div>}
        {dette.motif && <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 4 }}>Motif : {dette.motif}</div>}
      </div>
    );
  }

  // --- Carte soldée : soldee, solde, terminee, rembourse, cloturee ---
  if (STATUTS_SOLDES.has(dette.statut)) {
    return (
      <div style={{ background: '#fff', border: '1px solid #E6E6E6', borderLeft: '4px solid #9E9E9E', borderRadius: 12, padding: '14px 16px', marginBottom: 12, opacity: 0.75 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <IcoDette type={dette.type_dette} />
            <span style={{ fontWeight: 600, fontSize: 15, color: '#1A1A1A' }}>{dette.type_libelle}</span>
          </div>
          <BadgeStatut statut={dette.statut} />
        </div>
        {dateDecision && <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 4 }}>Clôturé le {dateDecision}</div>}
        <div style={{ fontSize: 12, color: '#9E9E9E' }}>Dossier terminé · entièrement remboursé</div>
      </div>
    );
  }

  // --- Carte active : comportement complet ---
  const suspendue = dette.statut === 'suspendue';
  const borderColor = suspendue ? '#9E9E9E' : '#1B5E20';

  return (
    <div style={{ background: '#fff', border: '1px solid #E6E6E6', borderLeft: `4px solid ${borderColor}`, borderRadius: 12, padding: '16px 16px 14px', boxShadow: '0 1px 2px rgba(15,15,15,0.04)', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <IcoDette type={dette.type_dette} />
          <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: '-0.005em', color: '#1A1A1A' }}>{dette.type_libelle}</span>
        </div>
        <BadgeStatut statut={dette.statut} />
      </div>

      <BarreProgression rembourse={dette.montant_rembourse} initial={dette.montant_initial} suspendue={suspendue} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>Il vous reste</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{formatFCFA(dette.montant_restant)}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>Chaque mois</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{formatFCFA(dette.mensualite_actuelle)}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>Fin prévue</div>
          <div style={{ fontSize: 13, color: '#1A1A1A' }}>{fmtDateMoisAnnee(dette.date_fin_prevue)}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>Mois restants</div>
          <div style={{ fontSize: 13, color: '#1A1A1A' }}>{dette.mois_restants} mois</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
        <span style={{ fontSize: 12, color: '#7A7A7A' }}>Prochain prélèvement : {labelProchainRet}</span>
        <button
          onClick={onToggle}
          style={{ background: 'transparent', border: 'none', color: '#144A18', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 0', minHeight: 44, fontFamily: 'inherit' }}
        >
          {ouvert ? 'Réduire' : 'Voir le détail'}
          <Ico name={ouvert ? 'chevDown' : 'chevR'} size={14} color="#144A18" />
        </button>
      </div>

      {ouvert && <DetailExpandable dette={dette} />}
    </div>
  );
}

function MetriquesGrid({ dettes }) {
  const actives = dettes.filter(d => d.statut === 'active');
  const totalRestant = actives.reduce((s, d) => s + (d.montant_restant || 0), 0);
  const retenuesMois = actives.reduce((s, d) => s + (d.mensualite_actuelle || 0), 0);
  const { label: labelPrelevement, jours } = prochainPrelevement();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '4px 0 18px' }}>
      {[
        { icon: 'creditCard', label: 'En cours de remboursement', value: actives.length, sub: 'en cours de remboursement', accent: false },
        { icon: 'dollar', label: 'Total restant', value: new Intl.NumberFormat('fr-FR').format(Math.round(totalRestant)), sub: 'FCFA à rembourser', accent: true },
        { icon: 'activity', label: 'Prélevé sur ma paie', value: new Intl.NumberFormat('fr-FR').format(Math.round(retenuesMois)), sub: 'FCFA sur votre paie', accent: false },
        { icon: 'calCheck', label: 'Prélevé le 15', value: labelPrelevement, sub: `dans ${jours} jours`, accent: false, small: true },
      ].map(({ icon, label, value, sub, accent, small }) => (
        <div key={label} style={{ background: '#fff', border: '1px solid #E6E6E6', borderRadius: 12, padding: '14px 14px 13px', boxShadow: '0 1px 2px rgba(15,15,15,0.04)' }}>
          <div style={{ color: '#7A7A7A', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Ico name={icon} size={14} color="#7A7A7A" />
            {label}
          </div>
          <div style={{ fontSize: small ? 17 : 20, fontWeight: 700, letterSpacing: '-0.01em', color: accent ? '#144A18' : '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>{sub}</div>
        </div>
      ))}
    </div>
  );
}

function MessagesFinanciers({ messages }) {
  if (!messages?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #E6E6E6', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,15,15,0.04)', marginTop: 14 }}>
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #EFEFEF' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ico name="mail" size={15} color="#7A7A7A" />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Messages financiers</span>
        </div>
      </div>
      {messages.map((m, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, padding: '12px 16px', borderBottom: i < messages.length - 1 ? '1px solid #EFEFEF' : 'none' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: m.lu ? '#BDBDBD' : '#1B5E20', marginTop: 7, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.45 }}>{m.texte}</div>
            <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>{m.date}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NoteInformative() {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#F1F6F1', border: '1px solid #DDEADC', borderRadius: 12, padding: '12px 14px', marginTop: 14, color: '#144A18', fontSize: 13, lineHeight: 1.45 }}>
      <Ico name="info" size={18} color="#1B5E20" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>Vos retenues sont automatiquement prélevées chaque <strong>15 du mois</strong> sur votre salaire. Aucune démarche n'est nécessaire de votre part.</span>
    </div>
  );
}

function ContactRH() {
  return (
    <div style={{ background: '#fff', border: '1px solid #E6E6E6', borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', marginTop: 14, boxShadow: '0 1px 2px rgba(15,15,15,0.04)' }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#E8F1E9', color: '#144A18', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Ico name="phone" size={18} color="#144A18" />
      </div>
      <div style={{ fontSize: 13, color: '#4A4A4A', lineHeight: 1.45, flex: 1 }}>
        <strong style={{ color: '#1A1A1A', display: 'block', marginBottom: 2 }}>Une question sur vos retenues ?</strong>
        Contactez le service RH.
      </div>
    </div>
  );
}

// ─── Normalisation API → format composants ────────────────────────────────
function normaliserMaDette(d) {
  return {
    ...d,
    type_dette: d.type_dette_code || '',
    type_libelle: d.type_dette_libelle || '—',
    montant_initial:   parseFloat(d.montant_initial)   || 0,
    montant_rembourse: parseFloat(d.montant_rembourse) || 0,
    montant_restant:   parseFloat(d.montant_restant)   || 0,
    mensualite_actuelle: parseFloat(d.mensualite) || 0,
    historique: [],
  };
}

// ─── Mon solde IPM ────────────────────────────────────────────────────────
// Socle IPM (3 octobre 2026) : relevé commun « Mon IPM » (dette = restes à charge).
function MonSoldeIPM({ token }) {
  return <div style={{ marginTop: 18 }}><MonIpm token={token} masquerSiVide /></div>;
}

// ─── Composant principal ─────────────────────────────────────────────────
const STATUTS_EN_COURS = new Set(['soumis', 'approuve_dg', 'traite_rh', 'transmis_compta']);
const STATUTS_TERMINAUX = new Set(['refusee', 'refuse', 'annulee', 'annule']);
const STATUTS_SOLDES    = new Set(['soldee', 'solde', 'terminee', 'rembourse', 'cloturee']);

export default function MesPrêtsPage() {
  const { employe, token } = useAuth();
  const navigate = useNavigate();
  const [detteOuverte, setDetteOuverte] = useState(null);
  const [dettes, setDettes] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/dettes/employe/mes-dettes`, { headers }),
      axios.get(`${API}/prets-sogas/mes-demandes`, { headers }),
    ])
      .then(([resD, resP]) => {
        const raw = Array.isArray(resD.data.dettes) ? resD.data.dettes : [];
        setDettes(raw.map(normaliserMaDette));
        const rawD = Array.isArray(resP.data.demandes) ? resP.data.demandes : [];
        setDemandes(rawD.filter(d => STATUTS_EN_COURS.has(d.statut)));
      })
      .catch(e => setErreur(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleDette = id => setDetteOuverte(prev => prev === id ? null : id);

  const today = aujourd_hui();

  return (
    <Layout>
      <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#f7f7f7' }}>

        {/* ── Header vert ── */}
        <div className="sg-bandeau-clair" style={{ background: '#1B5E20', padding: '14px 18px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 12, fontFamily: 'inherit' }}
            >
              <Ico name="backArrow" size={14} color="#fff" />
              Retour
            </button>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.65)', fontWeight: 600, marginBottom: 4 }}>
              Espace employé
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', margin: '0 0 4px' }}>
              Mon Argent
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
              <Ico name="calendar" size={13} color="rgba(255,255,255,0.65)" />
              <span style={{ textTransform: 'capitalize' }}>{today}</span>
            </div>
          </div>
        </div>

        {/* ── Corps ── */}
        <div style={{ padding: '18px 16px 48px', maxWidth: 720, margin: '0 auto' }}>

          {loading && <div style={{ textAlign: 'center', padding: 40, color: '#7A7A7A', fontSize: 14 }}>Chargement…</div>}
          {erreur && <div style={{ background: '#FFEBEE', borderRadius: 8, padding: '12px 14px', color: '#C62828', fontSize: 13, marginBottom: 14 }}>Erreur : {erreur}</div>}

          {/* Métriques */}
          {!loading && <MetriquesGrid dettes={dettes} />}

          {/* Liste des dettes — la dette IPM appartient au module IPM (relevé « Mon IPM ») */}
          {/* TODO_REACTIVER: retirer le filtre gestionnaire si la logique métier change */}
          {!loading && <div style={{ marginTop: 18 }}>
            {(() => {
              const dettesVisibles = dettes.filter(d => d.gestionnaire !== 'ipm');
              const now = Date.now();
              const SEPT_JOURS_MS = 7 * 24 * 60 * 60 * 1000;
              const actives = dettesVisibles.filter(d => !STATUTS_TERMINAUX.has(d.statut) && !STATUTS_SOLDES.has(d.statut));
              const historique = dettesVisibles.filter(d =>
                (STATUTS_TERMINAUX.has(d.statut) || STATUTS_SOLDES.has(d.statut)) &&
                d.updated_at && (now - new Date(d.updated_at).getTime()) < SEPT_JOURS_MS
              );
              /* TODO_HISTORIQUE_COMPLET: les dettes terminées/soldées de plus de 7 jours sont masquées — implémenter une vue "Historique complet" si besoin */
              return (
                <>
                  {/* SECTION 1 — Ce que je rembourse en ce moment */}
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.005em', padding: '0 4px 10px' }}>
                    Ce que je rembourse en ce moment
                  </div>
                  {actives.length === 0
                    ? <div style={{ textAlign: 'center', padding: '18px 0 24px', color: '#7A7A7A', fontSize: 14 }}>Tout est à jour — vous n'avez rien à rembourser en ce moment.</div>
                    : actives.map(dette => (
                        <CarteDette key={dette.id} dette={dette} ouvert={detteOuverte === dette.id} onToggle={() => toggleDette(dette.id)} />
                      ))
                  }

                  {/* SECTION 2 — Historique récent (< 7 jours) */}
                  {historique.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.005em', padding: '0 4px 10px' }}>
                        Historique récent
                      </div>
                      {historique.map(dette => (
                        <CarteDette key={dette.id} dette={dette} ouvert={detteOuverte === dette.id} onToggle={() => toggleDette(dette.id)} />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>}

          {/* Mon solde IPM */}
          {!loading && <MonSoldeIPM token={token} />}

          {/* Demandes de prêt SOGAS en cours */}
          {!loading && demandes.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #E6E6E6', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,15,15,0.04)', marginTop: 14 }}>
              <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #EFEFEF' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Ico name="clock" size={15} color="#7A7A7A" />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Demandes en cours</span>
                </div>
              </div>
              {demandes.map((d, i) => (
                <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, padding: '12px 16px', borderBottom: i < demandes.length - 1 ? '1px solid #EFEFEF' : 'none', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>
                      Prêt SOGAS — {Number(d.montant_demande).toLocaleString('fr-FR')} FCFA
                    </div>
                    <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>
                      {d.numero_dossier} · {d.duree_mois} mois
                    </div>
                    {suiviEnCours(d.suivi) && <SuiviDemande suivi={d.suivi} />}
                  </div>
                  <span style={{ background: '#FFF3E0', color: '#E65100', padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {STATUTS_DEMANDE_LABEL[d.statut] || d.statut}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Note informative */}
          <NoteInformative />

          {/* Contact RH */}
          <ContactRH />

          <p style={{ textAlign: 'center', fontSize: 11, color: '#BDBDBD', margin: '18px 0 0', paddingBottom: 8 }}>
            © {new Date().getFullYear()} SOGAS — Données consultatives
          </p>
        </div>
      </div>
    </Layout>
  );
}
